import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { DiaryActionChip } from '@/components/DiaryHeader/DiaryActionChip';
import { EmptyState } from '@/components/EmptyState';
import { TestPaper } from '@/components/Icons/TestPaper';
import { EvaluationChartPaywall } from '@/components/LessonDetailsModal/EvaluationChartPaywall';
import { EvaluationScaleSheet } from '@/components/LessonDetailsModal/EvaluationScaleSheet';
import { EvaluationScaleTable } from '@/components/LessonDetailsModal/EvaluationScaleTable';
import { LessonDetailsSection } from '@/components/LessonDetailsModal/LessonDetailsSection';
import { TestQuestionCard } from '@/components/LessonDetailsModal/TestQuestionCard';
import { TestReportSubjectCard } from '@/components/LessonDetailsModal/TestReportSubjectCard';
import { TestScoreCard } from '@/components/LessonDetailsModal/TestScoreCard';
import { useTestReport } from '@/components/LessonDetailsModal/hooks/useTestReport';
import { ModalHeader } from '@/components/Modal/ModalHeader';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { MODAL_OPEN_ANIMATION_MS } from '@/components/Modal/constants';
import { useIsOpenAnimationDone } from '@/hooks/useIsOpenAnimationDone';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useShowSubscriptionOffer } from '@/hooks/useShowSubscriptionOffer';
import { StudentRankingCard } from '@/pages/Overview/StudentRankingCard';
import type { Attachment } from '@/types/attachment';
import { EvaluationChartType } from '@/types/ratings';
import type { TestReport } from '@/types/testReport';
import { parseDecimal, withPercentSign } from '@/utils/text';

interface Props {
  testId: number;
  onBack: () => void;
  onClose: () => void;
}

const toRankingChart = (
  { chartItems }: TestReport,
  classPercentage: string,
) => ({
  disciplineId: null,
  disciplineName: null,
  averageClassEvaluationTotal: {
    value: parseDecimal(classPercentage),
    stringValue: classPercentage,
  },
  chartValues: chartItems.map(({ value, isCurrentUser }) => ({
    evaluation: { value: parseDecimal(value), stringValue: `${value}%` },
    isCurrentStudent: isCurrentUser,
  })),
});

export const TestReportView: React.FC<Props> = ({
  testId,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation();
  const hasSubscription = useHasFamilySubscription();
  const showSubscriptionOffer = useShowSubscriptionOffer();
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );
  const [isScaleSheetOpen, setIsScaleSheetOpen] = useState(false);

  const { progress, handleScroll } = useScrollFadeProgress();
  const { data: report, isLoading } = useTestReport(testId);
  const [isDataCachedOnOpen] = useState(!isLoading);
  const isOpenAnimationDone = useIsOpenAnimationDone(MODAL_OPEN_ANIMATION_MS);

  const title = t('diary.testReport.title');
  const isScrolled = progress > 0.5;
  const isContentReady =
    !isLoading && (isDataCachedOnOpen || isOpenAnimationDone);

  const questions = [...(report?.testQuestionReportItems ?? [])].sort(
    (question, otherQuestion) =>
      question.orderNumber - otherQuestion.orderNumber,
  );
  const classPercentage = report
    ? withPercentSign(report.averageClassPointPercentage)
    : '';
  const isPercentEvaluation =
    report?.evaluationType === EvaluationChartType.Percent;
  const hasScale = !!report?.evaluationScaleItems.length;
  const hasPercentageChart =
    hasSubscription &&
    !!report?.chartItems.some(({ isCurrentUser }) => isCurrentUser) &&
    parseDecimal(classPercentage) > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ModalHeader
        isMobileTitleHidden={!isScrolled}
        title={title}
        onBack={onBack}
        onClose={onClose}
      />

      {!isContentReady && (
        <div className="flex flex-1 items-center justify-center">
          <PageSpinner className="size-[22px] text-subtle-foreground" />
        </div>
      )}

      {isContentReady && !report && (
        <EmptyState label={t('diary.testReport.notFound')} />
      )}

      {isContentReady && !!report && (
        <AutoHideScrollArea
          hasTopShadow
          className="flex min-h-0 flex-1 flex-col"
          onScroll={handleScroll}
        >
          <div className="flex flex-col gap-5 px-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:px-5 md:pb-6">
            <h1 className="pt-3 pb-2 text-[24px]/[30px] font-semibold text-foreground md:hidden">
              {title}
            </h1>

            <TestReportSubjectCard
              report={report}
              onPreview={setPreviewAttachment}
            />

            {showSubscriptionOffer && (
              <div className="rounded-xl bg-card px-4 py-6 shadow-card contrast:border contrast:border-border md:p-0">
                <EvaluationChartPaywall
                  description={t('diary.testReport.paywallDescription')}
                />
              </div>
            )}

            <div className="flex flex-col gap-5 md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                <LessonDetailsSection
                  title={t('diary.testReport.questions')}
                  action={
                    hasScale && (
                      <DiaryActionChip
                        className="md:hidden"
                        label={t('diary.testReport.scale')}
                        icon={<TestPaper className="size-4" />}
                        onClick={() => setIsScaleSheetOpen(true)}
                      />
                    )
                  }
                >
                  {questions.map((question) => (
                    <TestQuestionCard
                      key={question.orderNumber}
                      question={question}
                      hasSubscription={hasSubscription}
                    />
                  ))}
                </LessonDetailsSection>
              </div>

              <div className="flex flex-col gap-3 max-md:order-first md:w-[285px] md:shrink-0">
                <LessonDetailsSection
                  isMobileTitleHidden
                  title={t('diary.evaluation.title')}
                >
                  <TestScoreCard
                    isEvaluationBadge
                    title={t('diary.testReport.studentEvaluation')}
                    evaluation={report.studentEvaluation}
                    points={report.totals.pointsOfStudent}
                    percentage={
                      isPercentEvaluation
                        ? undefined
                        : report.studentPointPercentage
                    }
                  />
                  {hasSubscription && (
                    <TestScoreCard
                      title={t('diary.testReport.classAverage')}
                      evaluation={report.averageClassEvaluation ?? ''}
                      points={report.totals.averageClassPoints}
                      percentage={
                        isPercentEvaluation ? undefined : classPercentage
                      }
                    />
                  )}
                </LessonDetailsSection>

                {hasScale && (
                  <div className="hidden flex-col gap-3 md:flex">
                    <h3 className="text-subhead-14 text-foreground">
                      {t('diary.testReport.scale')}
                    </h3>
                    <EvaluationScaleTable items={report.evaluationScaleItems} />
                  </div>
                )}
              </div>
            </div>

            {hasPercentageChart && (
              <StudentRankingCard
                chart={toRankingChart(report, classPercentage)}
                type={EvaluationChartType.Percent}
                title={t('diary.testReport.percentageChart')}
              />
            )}
          </div>
        </AutoHideScrollArea>
      )}

      {!!report && hasScale && (
        <EvaluationScaleSheet
          isOpen={isScaleSheetOpen}
          items={report.evaluationScaleItems}
          onClose={() => setIsScaleSheetOpen(false)}
        />
      )}

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onDownloaded={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
};
